import React from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import { FetchContext } from '../context/FetchContext'
import '../Styles/AdminDebtors.css'

const formatPerson = (debtor) => debtor.fio || debtor.email || 'Без имени'

const formatDate = (value) => {
    if (!value) return ''

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return value
    }

    return date.toLocaleDateString('ru-RU')
}

function AdminDebtors() {
    const auth = React.useContext(AuthContext)
    const fetchContext = React.useContext(FetchContext)
    const navigate = useNavigate()

    const [query, setQuery] = React.useState('')
    const [results, setResults] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState('')
    const [message, setMessage] = React.useState('')
    const [passwordTarget, setPasswordTarget] = React.useState(null)
    const [newPassword, setNewPassword] = React.useState('')
    const [newPasswordConfirm, setNewPasswordConfirm] = React.useState('')
    const [passwordSaving, setPasswordSaving] = React.useState(false)
    const [deleteTarget, setDeleteTarget] = React.useState(null)
    const [deleteConfirm, setDeleteConfirm] = React.useState('')
    const [deleting, setDeleting] = React.useState(false)

    const loadDebtors = React.useCallback(async (value) => {
        const q = value.trim()

        if (q.length < 2) {
            setResults([])
            setError('')
            return
        }

        try {
            setLoading(true)
            setError('')
            const { data } = await fetchContext.authAxios.get('api/admin/debtors/search', {
                params: { q },
            })
            setResults(data || [])
        } catch (err) {
            console.log(err)
            setResults([])
            setError(err.response?.data?.message || 'Не удалось выполнить поиск')
        } finally {
            setLoading(false)
        }
    }, [fetchContext])

    React.useEffect(() => {
        const timeout = setTimeout(() => loadDebtors(query), 350)
        return () => clearTimeout(timeout)
    }, [loadDebtors, query])

    const openDebtorCabinet = async (email) => {
        if (!email) return

        try {
            setLoading(true)
            setError('')
            const { data } = await fetchContext.authAxios.post('api/admin/impersonation/start', { email })
            auth.startImpersonation(data)
            navigate('/dc')
        } catch (err) {
            console.log(err)
            setError(err.response?.data?.message || 'Не удалось открыть кабинет должника')
        } finally {
            setLoading(false)
        }
    }

    const requestPasswordChange = (debtor) => {
        setPasswordTarget(debtor)
        setNewPassword('')
        setNewPasswordConfirm('')
        setError('')
        setMessage('')
    }

    const cancelPasswordChange = () => {
        setPasswordTarget(null)
        setNewPassword('')
        setNewPasswordConfirm('')
    }

    const changeDebtorPassword = async () => {
        if (!passwordTarget) return

        if (newPassword.length < 6) {
            setError('Пароль должен содержать минимум 6 символов')
            return
        }

        if (newPassword !== newPasswordConfirm) {
            setError('Пароли не совпадают')
            return
        }

        try {
            setPasswordSaving(true)
            setError('')
            const { data } = await fetchContext.authAxios.patch(
                `api/admin/debtors/${encodeURIComponent(passwordTarget.email)}/password`,
                { password: newPassword }
            )

            setMessage(data.message || 'Пароль пользователя изменен')
            cancelPasswordChange()
        } catch (err) {
            console.log(err)
            setError(err.response?.data?.message || 'Не удалось изменить пароль пользователя')
        } finally {
            setPasswordSaving(false)
        }
    }

    const requestDelete = (debtor) => {
        setDeleteTarget(debtor)
        setDeleteConfirm('')
        setError('')
        setMessage('')
    }

    const cancelDelete = () => {
        setDeleteTarget(null)
        setDeleteConfirm('')
    }

    const deleteDebtor = async () => {
        if (!deleteTarget || deleteConfirm.trim().toLowerCase() !== deleteTarget.email.toLowerCase()) {
            setError('Для удаления нужно ввести email должника полностью')
            return
        }

        try {
            setDeleting(true)
            setError('')
            const { data } = await fetchContext.authAxios.delete(
                `api/admin/debtors/${encodeURIComponent(deleteTarget.email)}`
            )

            setResults((items) => items.filter((item) => item.email !== deleteTarget.email))
            setMessage(data.message || 'Пользователь удален')
            cancelDelete()
        } catch (err) {
            console.log(err)
            setError(err.response?.data?.message || 'Не удалось удалить пользователя')
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div className="admin-debtors-page">
            <div className="admin-debtors-shell">
                <div className="admin-debtors-header">
                    <div>
                        <h1>Управление должниками</h1>
                        <p>Поиск пользователя, просмотр его личного кабинета и удаление связанных данных.</p>
                    </div>
                </div>

                <div className="admin-debtors-search">
                    <label htmlFor="admin-debtors-search">ФИО, email, номер договора или project_id</label>
                    <input
                        id="admin-debtors-search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Например: Иванов Иван, mail@example.ru, 04445412 или 316902"
                    />
                    <span>{query.trim().length < 2 ? 'Введите минимум 2 символа' : loading ? 'Поиск...' : `Найдено: ${results.length}`}</span>
                </div>

                {message && <div className="admin-debtors-message">{message}</div>}
                {error && <div className="admin-debtors-error">{error}</div>}

                <div className="admin-debtors-list">
                    {results.map((debtor) => (
                        <div className="admin-debtor-row" key={debtor.id || debtor.email}>
                            <div className="admin-debtor-info">
                                <strong>{formatPerson(debtor)}</strong>
                                <span>{debtor.email}</span>
                                {Array.isArray(debtor.contracts) && debtor.contracts.length > 0 && (
                                    <div className="admin-debtor-contracts">
                                        {debtor.contracts.slice(0, 4).map((contract) => (
                                            <span key={`${contract.UidSale}-${contract.agreementNum}`}>
                                                {contract.agreementNum || 'Без номера'} / project_id: {contract.UidSale}
                                            </span>
                                        ))}
                                        {debtor.contracts.length > 4 && (
                                            <span>Еще договоров: {debtor.contracts.length - 4}</span>
                                        )}
                                    </div>
                                )}
                                <span>{[formatDate(debtor.birthdate), debtor.phone].filter(Boolean).join(' | ') || 'Нет дополнительных данных'}</span>
                            </div>
                            <div className="admin-debtor-actions">
                                <button type="button" onClick={() => openDebtorCabinet(debtor.email)}>
                                    Открыть кабинет
                                </button>
                                <button
                                    type="button"
                                    className="secondary"
                                    onClick={() => requestPasswordChange(debtor)}
                                >
                                    Сменить пароль
                                </button>
                                <button
                                    type="button"
                                    className="danger"
                                    onClick={() => requestDelete(debtor)}
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    ))}

                    {!loading && query.trim().length >= 2 && results.length === 0 && (
                        <div className="admin-debtors-empty">Должники не найдены</div>
                    )}
                </div>
            </div>

            {passwordTarget && (
                <div className="admin-debtors-modal" role="dialog" aria-modal="true">
                    <div className="admin-debtors-modal-panel">
                        <h2>Сменить пароль</h2>
                        <p>
                            Новый пароль будет сохранен для выбранного должника. После этого пользователь сможет
                            авторизоваться с новым паролем.
                        </p>
                        <div className="admin-debtors-password-target">
                            <strong>{formatPerson(passwordTarget)}</strong>
                            <span>{passwordTarget.email}</span>
                        </div>
                        <label htmlFor="admin-debtors-new-password">
                            Новый пароль
                        </label>
                        <input
                            id="admin-debtors-new-password"
                            type="password"
                            value={newPassword}
                            onChange={(event) => setNewPassword(event.target.value)}
                            placeholder="Минимум 6 символов"
                            autoComplete="new-password"
                        />
                        <label htmlFor="admin-debtors-new-password-confirm">
                            Повторите пароль
                        </label>
                        <input
                            id="admin-debtors-new-password-confirm"
                            type="password"
                            value={newPasswordConfirm}
                            onChange={(event) => setNewPasswordConfirm(event.target.value)}
                            placeholder="Повторите новый пароль"
                            autoComplete="new-password"
                        />
                        <div className="admin-debtors-modal-actions">
                            <button type="button" onClick={cancelPasswordChange} disabled={passwordSaving}>
                                Отмена
                            </button>
                            <button
                                type="button"
                                onClick={changeDebtorPassword}
                                disabled={passwordSaving}
                            >
                                {passwordSaving ? 'Сохранение...' : 'Сохранить пароль'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <div className="admin-debtors-modal" role="dialog" aria-modal="true">
                    <div className="admin-debtors-modal-panel">
                        <h2>Удалить пользователя?</h2>
                        <p>
                            Будут удалены запись пользователя, его токены доступа к договорам и связанные документы.
                            Это действие нельзя отменить.
                        </p>
                        <div className="admin-debtors-delete-target">
                            <strong>{formatPerson(deleteTarget)}</strong>
                            <span>{deleteTarget.email}</span>
                        </div>
                        <label htmlFor="admin-debtors-delete-confirm">
                            Введите email для подтверждения
                        </label>
                        <input
                            id="admin-debtors-delete-confirm"
                            value={deleteConfirm}
                            onChange={(event) => setDeleteConfirm(event.target.value)}
                            placeholder={deleteTarget.email}
                        />
                        <div className="admin-debtors-modal-actions">
                            <button type="button" onClick={cancelDelete} disabled={deleting}>
                                Отмена
                            </button>
                            <button
                                type="button"
                                className="danger"
                                onClick={deleteDebtor}
                                disabled={deleting}
                            >
                                {deleting ? 'Удаление...' : 'Удалить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminDebtors
